import CryptoJS from 'crypto-js'

interface SecureCookieOptions {
  expires?: number // Days
  path?: string
  domain?: string
  sameSite?: 'strict' | 'lax' | 'none'
  encrypt?: boolean
  encryptionSecret?: string
}

export const useCookiee = {
  set<T = any>(
    name: string,
    value: T,
    options?: SecureCookieOptions,
  ): void {
    const config = useRuntimeConfig()
    const encrypt = options?.encrypt ?? false
    const secret = options?.encryptionSecret ?? config.public.cookieEncryptionSecret ?? ''

    const stringValue = typeof value === 'string' ? value : JSON.stringify(value)
    const finalValue = encrypt ? this._encode(stringValue, secret) : stringValue

    const isProduction = config.public.envSite === 'production'

    const cookie = useCookie(name, {
      maxAge: (options?.expires ?? 7) * 86400,
      path: options?.path ?? '/',
      domain: options?.domain,
      secure: isProduction,
      sameSite: options?.sameSite ?? 'strict',
    })

    cookie.value = finalValue
  },

  get<T = any>(name: string, options?: SecureCookieOptions): T | null {
    const config = useRuntimeConfig()
    const cookie = useCookie<string | null>(name, {
      default: () => null,
      watch: false,
    })

    const rawValue = cookie.value

    if (!rawValue)
      return null

    const encrypt = options?.encrypt ?? false
    const secret = options?.encryptionSecret ?? config.public.cookieEncryptionSecret ?? ''

    try {
      const decodedValue = encrypt ? this._decode(rawValue, secret) : rawValue
      return JSON.parse(decodedValue) as T
    }
    catch {
      return rawValue as T
    }
  },

  remove(name: string, options?: Pick<SecureCookieOptions, 'path' | 'domain'>): void {
    const cookie = useCookie<string | null>(name, {
      maxAge: -1,
      path: options?.path ?? '/',
      domain: options?.domain,
    })

    cookie.value = null
  },

  getAll(): Record<string, string> {
    if (import.meta.client) {
      const cookies: Record<string, string> = {}
      document.cookie.split(';').forEach((cookie) => {
        const [name, value] = cookie.split('=').map(c => c.trim())
        if (name && value) {
          cookies[name] = decodeURIComponent(value)
        }
      })
      return cookies
    }
    return {}
  },

  _encode(value: string, secret: string): string {
    try {
      return CryptoJS.AES.encrypt(value, secret).toString()
    }
    catch {
      return value
    }
  },

  _decode(value: string, secret: string): string {
    try {
      const bytes = CryptoJS.AES.decrypt(value, secret)
      return bytes.toString(CryptoJS.enc.Utf8)
    }
    catch {
      return value
    }
  },
}
